// src/components/locationCheck/LocationCheck.jsx
//
// Manager board: establishments whose position (scanned on site) is more
// than 300 m from the address on their record (utils/locationCheck.js), and
// the « Marquer non localisé » button - position erased, owner e-mailed
// (backend routes/adminUnlocate.ts).
import React, { useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Link, Typography,
} from '@mui/material';
import {
  LOCATION_MISMATCH_M, locationStatus, formatDistance, osmUrl, unlocateFacility,
} from '../../utils/locationCheck';

const STATUS = {
  ok: { color: 'success', label: (f) => formatDistance(f.address_distance_m) },
  far: { color: 'error', label: (f) => formatDistance(f.address_distance_m) },
  imprecise: { color: 'default', label: () => 'adresse imprécise' },
  notFound: { color: 'default', label: () => 'adresse introuvable' },
  notScanned: { color: 'default', label: () => 'position non scannée' },
  pending: { color: 'default', label: () => 'en attente' },
};

function UnlocateDialog({ facility, getToken, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!facility) return null;
  const confirm = async () => {
    setBusy(true); setError('');
    try {
      const r = await unlocateFacility(facility.id, getToken());
      onDone(facility, r);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Marquer « {facility.name} » non localisé ?</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 1 }}>
          La position est effacée et l’établissement repasse « non localisé » : ses notes sont refusées jusqu’à ce que
          le gérant rescanne le QR de localisation sur place.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Écart actuel : <b>{formatDistance(facility.address_distance_m)}</b> · adresse : {facility.address || '—'}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          E-mail envoyé à <b>{facility.owner_email || '—'}</b> (dans sa langue) :
        </Typography>
        <Box sx={{ bgcolor: '#f4f6fb', borderRadius: 1, p: 1.5, fontSize: 14 }} data-testid="unlocate-preview">
          <b>Action requise : relocalisez {facility.name} sur QRProofs</b>
          <p style={{ margin: '8px 0' }}>
            La position enregistrée pour {facility.name} se trouve à environ {formatDistance(facility.address_distance_m)} de
            l’adresse indiquée sur sa fiche ({facility.address || '—'}). Une note ne compte que si le client est dans
            l’établissement : les notes sont donc suspendues jusqu’à ce que l’établissement soit relocalisé.
          </p>
          <p style={{ margin: 0 }}>
            Pour corriger : sur place, ouvrir la page de l’établissement dans le tableau de bord et scanner le QR code de
            localisation avec le téléphone. Si l’adresse de la fiche est fausse, la corriger d’abord.
          </p>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>Échec : {error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Annuler</Button>
        <Button color="error" variant="contained" onClick={confirm} disabled={busy}>
          Marquer non localisé et envoyer l’e-mail
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The « Adresse ↔ position » cell of the Facilities table. */
export function LocationCell({ facility, onUnlocate }) {
  const status = locationStatus(facility);
  const s = STATUS[status];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      <Chip size="small" color={s.color} label={s.label(facility)} data-testid={`loc-${facility.id}`} />
      {status !== 'notScanned' && (
        <Button size="small" color="error" sx={{ minWidth: 0 }} onClick={() => onUnlocate(facility)}>
          Non localisé
        </Button>
      )}
    </Box>
  );
}

/** The alert at the top of the board: the far ones, with links to compare. */
export function LocationAlert({ facilities, onUnlocate }) {
  const far = facilities.filter((f) => locationStatus(f) === 'far')
    .sort((a, b) => Number(b.address_distance_m) - Number(a.address_distance_m));
  if (!far.length) return null;
  return (
    <Alert severity="warning" sx={{ mb: 3 }} data-testid="location-alert">
      <AlertTitle>
        {far.length} établissement{far.length > 1 ? 's' : ''} dont la position est à plus de {LOCATION_MISMATCH_M} m de l’adresse
      </AlertTitle>
      {far.map((f) => (
        <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.25 }}>
          <b>{f.name}</b> (#{f.id}) · <b>{formatDistance(f.address_distance_m)}</b> · {f.address}
          <Link href={osmUrl(f.address_lat, f.address_lon)} target="_blank" rel="noopener noreferrer">Voir l’adresse</Link>
          <Link href={osmUrl(f.lat, f.lon)} target="_blank" rel="noopener noreferrer">Voir la position</Link>
          <Button size="small" color="error" onClick={() => onUnlocate(f)}>Marquer non localisé</Button>
        </Box>
      ))}
      <Typography variant="caption" display="block" sx={{ mt: 1 }}>
        La position vient du scan sur place (SetLocation) : le gérant doit rescanner depuis l’établissement.
      </Typography>
    </Alert>
  );
}

/** Wires the alert, the cells and the dialog to the board's state. */
export function useUnlocate({ getToken, setFacilities }) {
  const [target, setTarget] = useState(null);
  const [notice, setNotice] = useState(null);
  const dialog = (
    <UnlocateDialog
      facility={target}
      getToken={getToken}
      onClose={() => setTarget(null)}
      onDone={(f, r) => {
        setTarget(null);
        setFacilities((list) => list.map((x) => (x.id === f.id
          ? { ...x, lat: 0, lon: 0, current_registration_step: 'unstarted', address_distance_m: null }
          : x)));
        setNotice({ name: f.name, emailSent: !!r.emailSent });
      }}
    />
  );
  const noticeEl = notice && (
    <Alert severity={notice.emailSent ? 'success' : 'warning'} sx={{ mb: 2 }} onClose={() => setNotice(null)} data-testid="unlocate-notice">
      « {notice.name} » marqué non localisé — {notice.emailSent ? 'e-mail envoyé au gérant.' : 'e-mail non envoyé (compte de test, établissement de test ou erreur d’envoi).'}
    </Alert>
  );
  return { open: setTarget, dialog, notice: noticeEl };
}
